import { loginResponseSchema } from '@proctoring/shared';
const storedSessionKeys = ['accessToken', 'expiresAt'];
/**
 * In-memory token holder with a `localStorage` mirror.
 *
 * Reads go through memory so a request never touches storage on the hot path, and every
 * mutation notifies subscribers — that is how a 401 in one query tears the whole app back
 * to the login screen rather than leaving stale UI behind a dead token.
 */
export class TokenStore {
    token = null;
    expiresAt = null;
    listeners = new Set();
    storageKey;
    storage;
    now;
    constructor({ storageKey, storage, now = () => new Date() }) {
        this.storageKey = storageKey;
        this.storage = storage;
        this.now = now;
        this.hydrate();
    }
    /** The current access token, or null when signed out or the stored one has expired. */
    get() {
        return this.token;
    }
    getExpiresAt() {
        return this.expiresAt;
    }
    set(session) {
        this.token = session.accessToken;
        this.expiresAt = session.expiresAt;
        try {
            this.storage?.setItem(this.storageKey, JSON.stringify(session));
        }
        catch {
            // A storage quota or private-mode failure must not break an otherwise valid login;
            // the session simply degrades to in-memory only.
        }
        this.emit();
    }
    clear() {
        this.token = null;
        this.expiresAt = null;
        try {
            this.storage?.removeItem(this.storageKey);
        }
        catch {
            // Ignored for the same reason as in `set`.
        }
        this.emit();
    }
    subscribe(listener) {
        this.listeners.add(listener);
        return () => {
            this.listeners.delete(listener);
        };
    }
    emit() {
        for (const listener of this.listeners)
            listener(this.token);
    }
    /**
     * Restores a persisted session on load, discarding anything malformed or already expired.
     *
     * Dropping expired tokens here rather than letting the API reject them saves a guaranteed
     * 401 round-trip on every reload after a session lapses.
     */
    hydrate() {
        let raw = null;
        try {
            raw = this.storage?.getItem(this.storageKey) ?? null;
        }
        catch {
            raw = null;
        }
        if (!raw)
            return;
        let parsed;
        try {
            parsed = JSON.parse(raw);
        }
        catch {
            this.clear();
            return;
        }
        if (typeof parsed !== 'object' || parsed === null) {
            this.clear();
            return;
        }
        const record = parsed;
        if (storedSessionKeys.some((key) => typeof record[key] !== 'string')) {
            this.clear();
            return;
        }
        const expiresAt = record.expiresAt;
        const expiry = new Date(expiresAt);
        if (Number.isNaN(expiry.getTime()) || expiry.getTime() <= this.now().getTime()) {
            this.clear();
            return;
        }
        this.token = record.accessToken;
        this.expiresAt = expiresAt;
    }
}
/** Narrows a login/setup-password response to the fields the token store persists. */
export function sessionFromLogin(response) {
    return { accessToken: response.accessToken, expiresAt: response.expiresAt };
}
export { loginResponseSchema };
//# sourceMappingURL=token-store.js.map